import React from 'react';
import styled from 'styled-components';
import { FaApple, FaGooglePlay } from 'react-icons/fa';

interface AppStoreBadgesProps {
  variant?: 'dark' | 'light';
  size?: 'sm' | 'md' | 'lg';
  direction?: 'row' | 'column';
  className?: string;
}

const APP_STORE_URL = 'https://apps.apple.com/us/app/pasur-online-play-score/id6476008595';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.vakilventures.pasur';

const sizeMap = {
  sm: { padding: '0.5rem 1rem', fontSize: '0.85rem', iconSize: '1.2rem', gap: '0.4rem' },
  md: { padding: '0.75rem 1.5rem', fontSize: '1rem', iconSize: '1.5rem', gap: '0.5rem' },
  lg: { padding: '1rem 2rem', fontSize: '1.1rem', iconSize: '1.75rem', gap: '0.6rem' },
};

const Container = styled.div<{ $direction: 'row' | 'column' }>`
  display: flex;
  flex-direction: ${props => props.$direction};
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
  justify-content: center;

  @media (max-width: 380px) {
    flex-direction: column;
    width: 100%;
  }
`;

const StoreButton = styled.a<{
  $variant: 'dark' | 'light';
  $size: 'sm' | 'md' | 'lg';
}>`
  display: inline-flex;
  align-items: center;
  gap: ${props => sizeMap[props.$size].gap};
  padding: ${props => sizeMap[props.$size].padding};
  min-height: 48px;
  border-radius: 8px;
  font-weight: 600;
  font-size: ${props => sizeMap[props.$size].fontSize};
  transition: background-color 150ms ease, border-color 150ms ease;
  border: 1px solid transparent;

  && {
    text-decoration: none;
    background: ${props => props.$variant === 'dark' ? '#1c124a' : '#fff6e8'};
    color: ${props => props.$variant === 'dark' ? '#fff6e8' : '#1c124a'};
    border-color: ${props => props.$variant === 'dark' ? '#1c124a' : '#e0d6c6'};
  }
  white-space: nowrap;

  svg {
    font-size: ${props => sizeMap[props.$size].iconSize};
    flex-shrink: 0;
  }

  &&:hover {
    background: ${props => props.$variant === 'dark' ? '#352768' : '#ffffff'};
    color: ${props => props.$variant === 'dark' ? '#fff6e8' : '#1c124a'};
    border-color: ${props => props.$variant === 'dark' ? '#352768' : '#2dd4bf'};
  }

  @media (max-width: 380px) {
    width: 100%;
    justify-content: center;
  }
`;

const ButtonContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.2;
`;

const SmallText = styled.span`
  font-size: 0.7em;
  font-weight: 400;
  opacity: 0.8;
`;

const MainText = styled.span`
  font-weight: 600;
`;

const AppStoreBadges: React.FC<AppStoreBadgesProps> = ({
  variant = 'dark',
  size = 'md',
  direction = 'row',
  className,
}) => {
  return (
    <Container $direction={direction} className={className}>
      <StoreButton
        href={APP_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        $variant={variant}
        $size={size}
        aria-label="Download on the App Store"
      >
        <FaApple aria-hidden="true" />
        <ButtonContent>
          <SmallText>Download on the</SmallText>
          <MainText>App Store</MainText>
        </ButtonContent>
      </StoreButton>

      <StoreButton
        href={PLAY_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        $variant={variant}
        $size={size}
        aria-label="Get it on Google Play"
      >
        <FaGooglePlay aria-hidden="true" />
        <ButtonContent>
          <SmallText>Get it on</SmallText>
          <MainText>Google Play</MainText>
        </ButtonContent>
      </StoreButton>
    </Container>
  );
};

export default AppStoreBadges;
